import React, { useCallback } from "react";
import Modal from "../common/Modal";
import AsyncSection from "../common/AsyncSection";
import RoomChip from "../common/RoomChip";
import useAdminResource from "../../hooks/useAdminResource";
import adminUserService from "../../services/adminUserService";
import ScheduleDetailBody, { ScheduleDetailBadges } from "./ScheduleDetailBody";
import DeviceDetailBody, { DeviceDetailBadges } from "./DeviceDetailBody";
import RoomDetailBody from "./RoomDetailBody";
import { deviceDisplayName, deviceNameOnly } from "../../utils/deviceName";

/**
 * Modal wrappers around the shared detail bodies.
 *
 * Each accepts either the full object (when the list already has it — no extra
 * request) or just an id (when another modal hands off, e.g. "view the schedule
 * that set this device's Smart Restore"). That is why the schedule modal can
 * fetch: without it, a hand-off would have to fall back to a page navigation
 * and the user would lose their place in the list.
 */

export function ScheduleDetailModal({ open, onClose, userId, schedule, scheduleId, deviceIndex }) {
  const needsFetch = open && !schedule && Boolean(scheduleId);

  const fetcher = useCallback(
    async () => ({ items: await adminUserService.fetchUserSchedule(userId, scheduleId) }),
    [userId, scheduleId]
  );

  const { data, state, error, refetch } = useAdminResource(fetcher, [userId, scheduleId], {
    enabled: needsFetch,
  });

  const resolved = schedule ?? data?.schedule ?? null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={
        <span className="flex items-center gap-2">
          {resolved?.name || "Schedule"}
          {resolved?.smartRestore && (
            <span title="Smart Restore is on" className="text-indigo-500">✨</span>
          )}
        </span>
      }
      subtitle={resolved ? String(resolved.scheduleId || resolved._id || "") : undefined}
      badges={resolved ? <ScheduleDetailBadges schedule={resolved} /> : null}
    >
      {needsFetch ? (
        <AsyncSection
          state={state}
          error={error}
          onRetry={() => refetch()}
          isEmpty={!resolved}
          loadingMessage="Loading schedule…"
          emptyTitle="Schedule not found"
          emptyMessage="It may have been deleted."
          compact
        >
          <ScheduleDetailBody schedule={resolved} deviceIndex={deviceIndex} />
        </AsyncSection>
      ) : (
        <ScheduleDetailBody schedule={resolved} deviceIndex={deviceIndex} />
      )}
    </Modal>
  );
}

export function DeviceDetailModal({
  open, onClose, userId, device, onOpenSchedule, onOpenRoom,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={deviceDisplayName(device, "Device")}
      // Not repeated when the title is already the id.
      subtitle={deviceNameOnly(device) ? device?.deviceId : undefined}
      badges={device ? <DeviceDetailBadges device={device} /> : null}
    >
      <DeviceDetailBody
        device={device}
        userId={userId}
        onOpenSchedule={onOpenSchedule}
        onOpenRoom={onOpenRoom}
      />
    </Modal>
  );
}

export function RoomDetailModal({ open, onClose, userId, room, onOpenDevice }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={room?.name || "Room"}
      badges={
        room ? (
          <>
            <RoomChip room={room} size="md" />
            <span className="text-xs text-slate-500">
              {room.totalDevices != null
                ? `${room.totalDevices} device${room.totalDevices === 1 ? "" : "s"}`
                : ""}
            </span>
          </>
        ) : null
      }
    >
      {room?._id && (
        <RoomDetailBody userId={userId} roomId={room._id} onOpenDevice={onOpenDevice} />
      )}
    </Modal>
  );
}
